import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { decodeJwt, type AuthResponse, type ClientRegisterPayload, type RegisterBusinessPayload } from '@getpay93/api';
import { api, tokenStore, lastOrgStore, setUnauthorizedHandler } from '../lib/api';
import { sessionFromAuth, sessionFromClaimsAndProfiles, type Session } from './session';

type Status = 'loading' | 'anon' | 'authed';

interface AuthState {
  status: Status;
  session: Session | null;
  login: (email: string, password: string) => Promise<Session>;
  registerBusiness: (payload: RegisterBusinessPayload) => Promise<Session>;
  registerClient: (payload: ClientRegisterPayload) => Promise<Session>;
  acceptInviteAndAuth: (args: { code: string; register?: { name: string; email: string; password: string } }) => Promise<Session>;
  switchOrg: (orgId: string) => Promise<Session>;
  logout: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const bootedRef = useRef(false);
  const queryClient = useQueryClient();

  function adopt(auth: AuthResponse): Session {
    const s = sessionFromAuth(auth);
    // Los datos cacheados pertenecen a la org anterior: descartarlos antes de renderizar.
    if ((auth.org?.id ?? null) !== (sessionRef.current?.org?.id ?? null)) queryClient.clear();
    tokenStore.set(auth.accessToken);
    if (auth.org?.id) lastOrgStore.set(auth.org.id);
    sessionRef.current = s;
    setSession(s);
    setStatus('authed');
    return s;
  }

  function logout() {
    queryClient.clear();
    tokenStore.clear();
    lastOrgStore.clear();
    sessionRef.current = null;
    setSession(null);
    setStatus('anon');
  }

  // 401 global → logout.
  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    return () => setUnauthorizedHandler(null);
  }, []);

  // Boot: si hay token, hidratar con /me/profiles.
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    const token = tokenStore.get();
    if (!token) {
      setStatus('anon');
      return;
    }
    const claims = decodeJwt(token);
    api.auth
      .meProfiles()
      .then((profiles) => {
        const s = sessionFromClaimsAndProfiles(token, claims ?? {}, profiles);
        sessionRef.current = s;
        setSession(s);
        setStatus('authed');
      })
      .catch(() => logout());
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      session,
      login: async (email, password) => {
        // Leer la última org ANTES del adopt: adopt sobrescribe lastOrgStore.
        const last = lastOrgStore.get();
        const s = adopt(await api.auth.login(email, password));
        if (last && last !== s.org?.id && s.profiles.some((p) => p.orgId === last)) {
          return adopt(await api.auth.switchOrg(last));
        }
        return s;
      },
      registerBusiness: async (payload) => adopt(await api.auth.registerBusiness(payload)),
      registerClient: async (payload) => adopt(await api.invites.clientRegister(payload)),
      acceptInviteAndAuth: async ({ code, register }) => {
        if (register) {
          // Cuenta nueva de cliente con el código: el backend acepta en la misma tx.
          return adopt(await api.invites.clientRegister({ ...register, inviteCode: code }));
        }
        // Ya autenticado: aceptar y re-hidratar la org recién unida.
        await api.invites.acceptInvite(code);
        const profiles = await api.auth.meProfiles();
        const target = profiles.find((p) => p.role === 'client') ?? profiles[0];
        if (target) return adopt(await api.auth.switchOrg(target.orgId));
        const token = tokenStore.get() ?? '';
        const s = sessionFromClaimsAndProfiles(token, decodeJwt(token) ?? {}, profiles);
        sessionRef.current = s;
        setSession(s);
        return s;
      },
      switchOrg: async (orgId) => adopt(await api.auth.switchOrg(orgId)),
      logout,
    }),
    [status, session],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
